/**
 * Sign-in as the app sees it. One interface, two implementations: Firebase
 * Auth in production (Google and email link, as today), a fake in tests.
 * Tokens are read fresh on demand; after claims change the app asks for a
 * forced refresh so the new org claims arrive.
 */
export interface AuthUser { uid: string; email: string | null; name: string | null; }
export interface AuthSession {
  /** Current user, or null; `onChange` fires on sign-in and sign-out. */
  current(): AuthUser | null;
  onChange(cb: (u: AuthUser | null) => void): () => void;
  idToken(forceRefresh?: boolean): Promise<string | null>;
  signInWithGoogle(): Promise<void>;
  sendSignInLink(email: string): Promise<void>;
  /** When the page was opened from an email link, finish the sign-in; returns whether it did. */
  completeSignInLink(): Promise<boolean>;
  signOut(): Promise<void>;
}

const EMAIL_KEY = 'rv-signin-email';

export function firebaseAuth(config: { apiKey: string; authDomain: string; projectId: string }, appUrl: string): AuthSession {
  // Loaded lazily so the test bundle never pulls Firebase in.
  const ready = (async () => {
    const { initializeApp } = await import('firebase/app');
    const { getAuth, GoogleAuthProvider, isSignInWithEmailLink, onAuthStateChanged, sendSignInLinkToEmail, signInWithEmailLink, signInWithPopup, signOut } = await import('firebase/auth');
    const auth = getAuth(initializeApp(config));
    return { auth, GoogleAuthProvider, isSignInWithEmailLink, onAuthStateChanged, sendSignInLinkToEmail, signInWithEmailLink, signInWithPopup, signOut };
  })();
  let current: AuthUser | null = null;
  const listeners = new Set<(u: AuthUser | null) => void>();
  void ready.then(({ auth, onAuthStateChanged }) => onAuthStateChanged(auth, (u) => { current = u ? { uid: u.uid, email: u.email, name: u.displayName } : null; for (const l of listeners) l(current); }));
  return {
    current: () => current,
    onChange: (cb) => { listeners.add(cb); return () => { listeners.delete(cb); }; },
    idToken: async (force) => { const { auth } = await ready; return auth.currentUser ? auth.currentUser.getIdToken(force) : null; },
    signInWithGoogle: async () => { const f = await ready; await f.signInWithPopup(f.auth, new f.GoogleAuthProvider()); },
    sendSignInLink: async (email) => { const f = await ready; await f.sendSignInLinkToEmail(f.auth, email, { url: appUrl, handleCodeInApp: true }); try { localStorage.setItem(EMAIL_KEY, email); } catch { /* private mode */ } },
    completeSignInLink: async () => {
      const f = await ready;
      if (!f.isSignInWithEmailLink(f.auth, window.location.href)) return false;
      let email: string | null = null; try { email = localStorage.getItem(EMAIL_KEY); } catch { /* private mode */ }
      if (!email) email = window.prompt('Confirm the email address you used'); // Firebase requires the address to finish an email-link sign-in
      if (!email) return false;
      await f.signInWithEmailLink(f.auth, email, window.location.href);
      try { localStorage.removeItem(EMAIL_KEY); } catch { /* private mode */ }
      history.replaceState(null, '', window.location.pathname);
      return true;
    },
    signOut: async () => { const f = await ready; await f.signOut(f.auth); },
  };
}

/** A sign-in that tests drive: `signIn(user, token)` fires the change, `idToken` returns what was set. */
export function fakeAuth(): AuthSession & { signIn(u: AuthUser, token: string): void; setToken(token: string): void; refreshes: number } {
  let current: AuthUser | null = null; let token: string | null = null;
  const listeners = new Set<(u: AuthUser | null) => void>();
  const api = {
    refreshes: 0,
    current: () => current,
    onChange: (cb: (u: AuthUser | null) => void) => { listeners.add(cb); return () => { listeners.delete(cb); }; },
    idToken: async (force?: boolean) => { if (force) api.refreshes++; return current ? token : null; },
    signInWithGoogle: async () => { api.signIn({ uid: 'u_google', email: 'g@example.com', name: 'G' }, 'google-token'); },
    sendSignInLink: async (email: string) => { if (!email.includes('@')) throw new Error('auth/invalid-email'); },
    completeSignInLink: async () => false,
    signOut: async () => { current = null; token = null; for (const l of listeners) l(null); },
    signIn: (u: AuthUser, t: string) => { current = u; token = t; for (const l of listeners) l(u); },
    setToken: (t: string) => { token = t; },
  };
  return api;
}
